import React, { useState, useEffect } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { Button, DialogActions, DialogContent, Paper, Stack, TextField, Typography } from '@mui/material';
import { RightioWBSAPIModelsCalls, RightioWBSAPIModelsCallsPatchInput, RightioWBSAPIModelsJobPatchInput, RightioWBSAPIModelsJobs, RightioWBSAPIModelsOtherPhoneCalls } from '@src/utilities/apis/wbs/model';
import { api, state } from '@src/interfaces/interfaces';
import { getISODate } from '@src/utilities/general';
import { dispatchSetSnackMsgError, dispatchSetSnackMsgSuccess } from '@src/reducers/uiReducer';
import ObjectsOperations from '@utils/objects/operations';

const name = "TeamLeaderFailModal";

const getJob = async (jobId = 0) => {
    if (jobId === 0) {
        return undefined;
    }

    const jobObject = await api.fetchJobByJobId(jobId);
    if (!jobObject) {
        return undefined;
    }

    return ObjectsOperations.deepCopyNestedObjectExpensive(jobObject);
};

const getCall = async (jobId = 0) => {
    if (jobId === 0) {
        return undefined;
    }

    const callObject = await api.fetchCallByJobId({ jobId });
    if (!callObject) {
        return undefined;
    }

    return ObjectsOperations.deepCopyNestedObjectExpensive(callObject);
}

const saveFailed = async (jobId: number, failFeedback: string, failFeedbackReason: number) => {
    // VERIFY WE CAPTURED JOB FAILURE NOTES
    if (failFeedback.length === 0) {
        dispatchSetSnackMsgError({
            ProblemMsg: "Please enter a reason for the job failure",
        });
        return false;
    }

    const job = await getJob(jobId);

    if (!job) {
        dispatchSetSnackMsgError({
            ProblemMsg: "Error retrieving job details",
        });
        return false;
    }

    const call = await getCall(jobId);

    if (!call) {
        dispatchSetSnackMsgError({
            ProblemMsg: "Error retrieving call details",
        });
        // return false;
    }

    console.log('job', job);

    job.statusId = 0;
    job.dateTimeOriginalRequired = undefined;
    job.dateTimeLatestArrival = undefined;
    job.dateTimeCommitted = getISODate(new Date());
    job.dateTimeUpdated = getISODate(new Date());
    job.notes = failFeedback;

    // SAVE JOB OBJECT TO TABLE
    await jobSubmit(job);

    if (call) {
        call.other = 1;
        call.otherPhoneId = failFeedbackReason;
        call.userId = job.soldBy || state.UserId;
        call.notes = failFeedback;
        call.dateTimeCreated = call.dateTimeCreated ? getISODate(new Date(call.dateTimeCreated)) : undefined;
        call.haukSale = undefined;
        call.accountCode = job.accountCode;
        call.jobId = job.jobId;
        await callSubmit(call);
    }

    return true;
};

const jobSubmit = async (job: RightioWBSAPIModelsJobs) => {
    const pureObject: RightioWBSAPIModelsJobPatchInput = {
        dateTimeOriginalRequired: job.dateTimeOriginalRequired,
        dateTimeLatestArrival: job.dateTimeLatestArrival,
        dateTimeCommitted: job.dateTimeCommitted,
        dateTimeUpdated: job.dateTimeUpdated,
        notes: job.notes,
        statusId: job.statusId,
        soldBy: job.soldBy,
        accountCode: job.accountCode,
    };

    if (job.jobId) {
        const patchedJob = await api.fetchJobsPatch(job.jobId, pureObject);
        if (patchedJob?.jobId && patchedJob.jobId > 0) {
            dispatchSetSnackMsgSuccess({
                ProblemMsg: "Job Patched",
            });
        } else {
            dispatchSetSnackMsgError({
                ProblemMsg: "Error updating job in DB",
            });
            return false;
        }
        return true;
    };
    return false;

}

const callSubmit = async (call: RightioWBSAPIModelsCalls) => {
    const pureObject: RightioWBSAPIModelsCallsPatchInput = {
        dateTimeCreated: call.dateTimeCreated,
        userId: call.userId,
        tradeCode: "",
        jobId: call.jobId,
        areaNumber: 0,
        booking: 0,
        reducedRate: 0,
        failed: 0,
        quote: 0,
        goBack: 0,
        other: call.other,
        haukSale: call.haukSale,
        accountCode: call.accountCode,
        otherPhoneId: call.otherPhoneId,
        notes: call.notes,
        advancedTypeId: 0,
    };

    if (call.callId) {
        const patchedCall = await api.fetchCallsPatch(call.callId, pureObject); // update
        if (patchedCall?.callId && patchedCall.callId > 0) {
            return true;
        }
        return false; // failed
    }
    return true; // no callId
};

type Props = {
    open: boolean;
    jobId: number;
    onClose: () => void;
    onSuccess: () => void;
    failFeedbackReason: RightioWBSAPIModelsOtherPhoneCalls | null;
};

const TeamLeaderFailModal: React.FC<Props> = ({ open, onClose, onSuccess, jobId, failFeedbackReason }) => {
    const [failFeedbackValue, setFailFeedbackValue] = useState<string>('');

    useEffect(() => {
        if (open) {
            // Reset all fields when the modal is opened
            setFailFeedbackValue('');
        }
    }, [open]);

    const handleCancel = () => {
        setFailFeedbackValue('');
        onSuccess();
        onClose();
    };

    const handleFail = async () => {
        let feedback = failFeedbackValue;

        const result = await saveFailed(jobId, feedback, failFeedbackReason?.otherPhoneId || 0);
        if (result) {
            handleCancel();
        } else {
            dispatchSetSnackMsgError({
                ProblemMsg: "Error saving job",
            });
        }
    };

    return (
        <Dialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
            <DialogTitle id="simple-dialog-title">Please provide feedback</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Paper elevation={4}>
                        <Typography variant="body1" sx={{ fontWeight: "bold", p: 1 }}>Please provide feedback for removing holding job.</Typography>
                    </Paper>
                    {failFeedbackReason?.question && (
                        <Paper elevation={4}>
                            <Typography variant="body1" sx={{ fontWeight: "bold", p: 1 }}>{failFeedbackReason.question}</Typography>
                        </Paper>
                    )}
                    <Paper elevation={4}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Reason for removal"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={failFeedbackValue}
                            onChange={(e) => setFailFeedbackValue(e.target.value)}
                            error={failFeedbackValue.length === 0}
                            multiline
                            rows={4}
                            sx={{ p: 1 }}
                        />
                    </Paper>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button color='success' variant='outlined' onClick={handleCancel}>Cancel</Button>
                <Button
                    color='error'
                    variant='contained'
                    onClick={handleFail}
                    disabled={failFeedbackValue.length === 0}
                >
                    Fail
                </Button>
            </DialogActions>
        </Dialog>
    );
};

TeamLeaderFailModal.displayName = name;

export default React.memo(TeamLeaderFailModal);
